import { isAbort, loadRosters } from '../data';
import { loc, pluralize, t } from '../i18n';
import { PLAYER_ROLES, RACES, type PlayerRole, type PlayerShort, type Race, type Roster } from '../types';
import { $, emptyState, errorState, h, loadingState, pad } from '../ui/dom';
import { playerChip, raceLabel, roleLabel, rosterRange } from '../ui/cards';

type Filters = { race: Race | null; role: PlayerRole | null; mmrMin: number | null; mmrMax: number | null };

let controller: AbortController | null = null;
let inputTimer: number | undefined;
let rosters: Roster[] = [];
// Survives remounts (language switch, back from a player card) within the session.
const filters: Filters = { race: null, role: null, mmrMin: null, mmrMax: null };

function isFiltering(): boolean {
  return filters.race != null || filters.role != null || filters.mmrMin != null || filters.mmrMax != null;
}

function matches(p: PlayerShort): boolean {
  if (filters.race && p.race !== filters.race) return false;
  if (filters.role && p.role !== filters.role) return false;
  if (filters.mmrMin != null && (p.mmr == null || p.mmr < filters.mmrMin)) return false;
  if (filters.mmrMax != null && (p.mmr == null || p.mmr > filters.mmrMax)) return false;
  return true;
}

function parseMmr(value: string): number | null {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function update(): void {
  renderFilters();
  renderList();
}

function chipGroup<T extends string>(
  label: string,
  values: T[],
  current: T | null,
  labelOf: (v: T) => string,
  set: (v: T | null) => void,
): HTMLElement {
  const btn = (v: T | null, text: string): HTMLElement =>
    h('button', {
      class: `chip${current === v ? ' active' : ''}`,
      text,
      attrs: { type: 'button', 'aria-pressed': String(current === v) },
      on: {
        click: () => {
          set(v);
          update();
        },
      },
    });
  return h(
    'div',
    'roster-filter',
    h('span', { class: 'roster-filter-label mono', text: label }),
    h('div', 'chips', btn(null, t('common.all')), ...values.map((v) => btn(v, labelOf(v)))),
  );
}

function mmrInput(placeholder: string, value: number | null, set: (v: number | null) => void): HTMLElement {
  return h('input', {
    attrs: { type: 'number', min: 0, step: 50, inputmode: 'numeric', placeholder, value: value ?? '', 'aria-label': `MMR ${placeholder}` },
    on: {
      input: (e) => {
        set(parseMmr((e.target as HTMLInputElement).value));
        window.clearTimeout(inputTimer);
        // Inputs are not re-rendered while typing to keep focus.
        inputTimer = window.setTimeout(() => {
          renderList();
          $('roster-filters')?.querySelector('.roster-reset')?.classList.toggle('hidden', !isFiltering());
        }, 200);
      },
    },
  });
}

function renderFilters(): void {
  const box = $('roster-filters');
  if (!box) return;
  box.replaceChildren(
    chipGroup(t('player.race'), RACES, filters.race, raceLabel, (v) => (filters.race = v)),
    chipGroup(t('player.role'), PLAYER_ROLES, filters.role, roleLabel, (v) => (filters.role = v)),
    h(
      'div',
      'roster-filter',
      h('span', { class: 'roster-filter-label mono', text: 'MMR' }),
      h(
        'div',
        'roster-mmr',
        mmrInput(t('roster.filter.from'), filters.mmrMin, (v) => (filters.mmrMin = v)),
        h('span', { class: 'muted', text: '—' }),
        mmrInput(t('roster.filter.to'), filters.mmrMax, (v) => (filters.mmrMax = v)),
      ),
    ),
    h('button', {
      class: `btn btn-ghost btn-sm roster-reset${isFiltering() ? '' : ' hidden'}`,
      text: t('roster.filter.reset'),
      attrs: { type: 'button' },
      on: {
        click: () => {
          Object.assign(filters, { race: null, role: null, mmrMin: null, mmrMax: null });
          update();
        },
      },
    }),
  );
}

function renderList(): void {
  const root = $('roster-list');
  const count = $('roster-count');
  if (!root) return;

  const filtering = isFiltering();
  const total = rosters.reduce((s, r) => s + r.players.length, 0);
  const view = rosters.map((r, i) => ({ r, i, players: filtering ? r.players.filter(matches) : r.players }));
  const found = view.reduce((s, v) => s + v.players.length, 0);

  if (count) {
    count.textContent = filtering
      ? `${t('roster.filter.found')}: ${found} / ${total}`
      : `${rosters.length} × ROSTER · ${total} ${pluralize(total, t('roster.players'))}`;
  }

  if (!rosters.length) {
    root.replaceChildren(emptyState());
    return;
  }

  const visible = filtering ? view.filter((v) => v.players.length) : view;
  if (!visible.length) {
    root.replaceChildren(emptyState('roster.filter.none'));
    return;
  }

  root.replaceChildren(
    ...visible.map(({ r, i, players }) => {
      const desc = loc(r, 'description');
      return h(
        'section',
        { class: 'roster', attrs: { id: `roster-${r.id}` } },
        h(
          'header',
          'roster-head',
          h('span', 'roster-index mono', pad(i + 1)),
          h('div', 'roster-title', h('h2', { text: r.name }), h('span', { class: 'mono roster-range', text: rosterRange(r) })),
          h(
            'div',
            'roster-count',
            h('b', null, filtering ? `${players.length}/${r.players.length}` : String(r.players.length)),
            h('span', { class: 'mono', text: pluralize(r.players.length, t('roster.players')) }),
          ),
        ),
        desc ? h('p', { class: 'roster-desc', text: desc }) : null,
        players.length
          ? h('div', 'roster-players', ...players.map(playerChip))
          : h('p', { class: 'muted roster-empty', text: t('roster.noPlayers') }),
      );
    }),
  );
}

export async function mountRoster(): Promise<void> {
  controller?.abort();
  controller = new AbortController();
  const signal = controller.signal;
  const root = $('roster-list');
  if (!root) return;
  root.replaceChildren(loadingState());
  $('roster-filters')?.replaceChildren();

  try {
    rosters = await loadRosters(signal);
    if (signal.aborted) return;
    update();

    // Deep link from home page: /roster#roster-<id>
    if (location.hash) document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (err) {
    if (!isAbort(err)) root.replaceChildren(errorState(() => void mountRoster()));
  }
}

export function unmountRoster(): void {
  controller?.abort();
  controller = null;
  window.clearTimeout(inputTimer);
  rosters = [];
}
